import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Check, Sparkles, Zap, ArrowRight } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../contexts";
import { toast } from "sonner";

const PERKS = [
  { icon: Zap, title: "Ad-Free Streaming", desc: "Every mixtape, every single, no breaks." },
  { icon: Sparkles, title: "Early Access Drops", desc: "Hear new mixtapes 7 days before the public." },
  { icon: Crown, title: "10% Off Everything", desc: "Auto-applied to all merch & books at checkout." },
  { icon: Check, title: "Verified Supporter Badge", desc: "Show up on artist profiles you tip with a badge." },
];

export default function CulturePassPage() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (!user) return setStatus(null);
    api.get("/culture-pass/status").then((r) => setStatus(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [user]);

  const subscribe = async () => {
    if (!user) { window.location.href = "/login"; return; }
    setBusy(true);
    try {
      const r = await api.post("/payments/culture-pass", { origin_url: window.location.origin });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start subscription.");
    } finally { setBusy(false); }
  };

  const formattedExpiry = status?.expires_at
    ? new Date(status.expires_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : null;

  return (
    <div className="pb-32" data-testid="culture-pass-page">
      {/* Hero */}
      <section className="relative grain-bg border-b border-neutral-800 overflow-hidden">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: "linear-gradient(180deg, rgba(5,5,5,0.4) 0%, rgba(5,5,5,0.95) 90%), url('https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 py-20 z-10">
          <div className="font-mono-label text-[11px] text-[var(--accent)] mb-4">CULTURE PASS / MONTHLY MEMBERSHIP</div>
          <h1 className="font-display text-6xl md:text-8xl uppercase leading-[0.9] max-w-3xl rise">
            $9 a Month.<br /><span className="text-[var(--accent)]">The Whole</span> Culture.
          </h1>
          <p className="text-neutral-300 max-w-2xl mt-6 text-lg rise rise-2">
            Ad-free streaming, early access to every drop, 10% off merch & books, and a verified supporter badge on every artist you tip.
          </p>
          <div className="mt-10 rise rise-3">
            {status?.active ? (
              <div className="space-y-4" data-testid="pass-active-banner">
                <div className="chip chip-accent text-[12px] py-2 px-3">
                  <Crown size={14} className="mr-1" /> MEMBER · ACTIVE TILL {formattedExpiry}
                </div>
                <div className="flex gap-3 flex-wrap">
                  <button onClick={subscribe} disabled={busy} className="btn-primary" data-testid="pass-renew-btn">
                    {busy ? "Redirecting…" : `Renew · $${status.price.toFixed(2)} / 30 days`}
                  </button>
                  <Link to="/mixtapes" className="btn-ghost">Stream Now <ArrowRight size={14} /></Link>
                </div>
              </div>
            ) : (
              <button onClick={subscribe} disabled={busy} className="btn-primary text-base !py-4 !px-6" data-testid="pass-subscribe-btn">
                <Crown size={16} /> {busy ? "Redirecting…" : `Get Culture Pass · $${(status?.price ?? 9).toFixed(2)}/mo`}
              </button>
            )}
            <p className="font-mono-label text-[10px] text-neutral-400 mt-4" data-testid="pass-no-autorenew">
              ONE-TIME $9 CHARGE · 30 DAYS OF FULL ACCESS · NO AUTO-RENEW
            </p>
            {!user && (
              <p className="font-mono-label text-[10px] text-neutral-400 mt-2" data-testid="pass-signin-note">SIGN IN TO SUBSCRIBE</p>
            )}
          </div>
        </div>
      </section>

      {/* Perks */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16">
        <div className="font-mono-label text-[10px] text-[var(--accent)] mb-3">WHAT YOU GET</div>
        <h2 className="font-headline text-3xl md:text-4xl uppercase mb-10">The Pass Unlocks Everything.</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {PERKS.map((p) => (
            <div key={p.title} className="card-brut p-6" data-testid={`pass-perk-${p.title.replace(/\s+/g, "-").toLowerCase()}`}>
              <p.icon size={22} className="text-[var(--accent)] mb-4" />
              <div className="font-headline text-xl uppercase mb-2">{p.title}</div>
              <p className="text-neutral-400 text-sm">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ-ish strip */}
      <section className="border-t border-neutral-800">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-12 grid sm:grid-cols-3 gap-8">
          <div>
            <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">HOW IT WORKS</div>
            <p className="text-neutral-300 text-sm">One $9 charge unlocks 30 days of full access. Renew anytime — no auto-billing surprises.</p>
          </div>
          <div>
            <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">CANCEL ANYTIME</div>
            <p className="text-neutral-300 text-sm">No subscription auto-renews. Your pass simply expires after 30 days unless you renew it.</p>
          </div>
          <div>
            <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">SUPPORTS ARTISTS</div>
            <p className="text-neutral-300 text-sm">A share of every Culture Pass goes back into the platform that funds independent artists & mixtape drops.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
